import React, { useEffect, useRef, useState } from "react";
import api from "../../api/axios";
import { FiEye, FiX, FiChevronLeft, FiChevronRight, FiCalendar, FiUser, FiShoppingBag, FiSearch, FiFilter, FiRefreshCw } from "react-icons/fi";
import "./OrderHistory.css";

const OrderHistory = () => {
  /* ===== STATE ===== */
  const [inquiries, setInquiries]     = useState([]);
  const [stats, setStats]             = useState({ total: 0, byStatus: {} });
  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [loading, setLoading]         = useState(false);
  const [selected, setSelected]       = useState(null);
  const modalRef = useRef(null);
  const limit = 10;

  // ---- FILTERS ----
  const [filters, setFilters] = useState({
    search:         "",
    inquiryStatus:  "",
    startDate:      "",
    endDate:        "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);

  /* ===== API ===== */
  const fetchInquiries = async (pg = page, f = appliedFilters) => {
    setLoading(true);
    try {
      const params = { page: pg, limit, type: "inquiry" };
      if (f.search)        params.search        = f.search.trim();
      if (f.inquiryStatus) params.inquiryStatus = f.inquiryStatus;
      if (f.startDate)     params.startDate     = f.startDate;
      if (f.endDate)       params.endDate       = f.endDate;

      const res = await api.get("/admin/dashboard", { params });
      const data = res.data?.data?.inquiries;
      setInquiries(data?.history || []);
      setStats({ total: data?.total || 0, byStatus: data?.byStatus || {} });
      setTotalPages(data?.pagination?.totalPages || 1);
    } catch (err) {
      console.error("Fetch inquiries error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when page or appliedFilters change
  useEffect(() => { fetchInquiries(page, appliedFilters); }, [page, appliedFilters]);  // eslint-disable-line

  /* ===== FILTER HANDLERS ===== */
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const handleClearFilters = () => {
    const empty = { search: "", inquiryStatus: "", startDate: "", endDate: "" };
    setFilters(empty);
    setPage(1);
    setAppliedFilters(empty);
  };

  const hasActiveFilter = appliedFilters.search || appliedFilters.inquiryStatus || appliedFilters.startDate || appliedFilters.endDate;

  /* ===== CLOSE MODAL ON OUTSIDE CLICK ===== */
  useEffect(() => {
    const handleOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) setSelected(null);
    };
    if (selected) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [selected]);

  /* ===== HELPERS ===== */
  const fmt = (dateStr) =>
    dateStr ? new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "N/A";

  const statusClass = (s) => {
    const map = { new: "oh-status-new", "in-progress": "oh-status-inprogress", won: "oh-status-won", lost: "oh-status-lost", closed: "oh-status-closed" };
    return map[s] || "oh-status-new";
  };

  const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "N/A");

  /* ===== STAT CARDS ===== */
  const statCards = [
    { label: "Total Inquiries",    value: stats.total,               color: "#6366f1" },
    { label: "New",                value: stats.byStatus?.new || 0,  color: "#f59e0b" },
    { label: "In Progress",        value: stats.byStatus?.["in-progress"] || 0, color: "#3b82f6" },
    { label: "Won",                value: stats.byStatus?.won || 0,  color: "#10b981" },
    { label: "Lost",               value: stats.byStatus?.lost || 0, color: "#ef4444" },
    { label: "Closed",             value: stats.byStatus?.closed || 0, color: "#6b7280" },
  ];

  /* ===== RENDER ===== */
  return (
    <div className="oh-page">
      <div className="oh-content">

        {/* ---- HEADER ---- */}
        <div className="oh-header">
          <h1>Order History</h1>
          <p className="oh-subtitle">Manage and track all inquiry orders</p>
        </div>

        {/* ---- STAT CARDS ---- */}
        <div className="oh-stats">
          {statCards.map((c) => (
            <div key={c.label} className="oh-stat-card" style={{ "--accent": c.color }}>
              <div className="oh-stat-number" style={{ color: c.color }}>{c.value}</div>
              <div className="oh-stat-label">{c.label}</div>
            </div>
          ))}
        </div>

        {/* ---- TABLE SECTION ---- */}
        <div className="oh-section">
          <div className="oh-section-header">
            <h3>Inquiry Listing</h3>
            <span className="oh-total-badge">Total: {stats.total}</span>
          </div>

          {/* ---- FILTERS ---- */}
          <div className="oh-filters">
            {/* Search */}
            <div className="oh-filter-field oh-filter-search">
              <FiSearch className="oh-filter-icon" />
              <input
                type="text"
                placeholder="Search by name…"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
              />
            </div>

            {/* Status */}
            <div className="oh-filter-field">
              <FiFilter className="oh-filter-icon" />
              <select
                value={filters.inquiryStatus}
                onChange={(e) => handleFilterChange("inquiryStatus", e.target.value)}
              >
                <option value="">All Status</option>
                <option value="new">New</option>
                <option value="in-progress">In Progress</option>
                <option value="won">Won</option>
                <option value="lost">Lost</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {/* Date range */}
            <div className="oh-filter-field">
              <FiCalendar className="oh-filter-icon" />
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange("startDate", e.target.value)}
                title="Start Date"
              />
            </div>
            <div className="oh-filter-field">
              <FiCalendar className="oh-filter-icon" />
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
                title="End Date"
              />
            </div>

            {/* Action buttons */}
            <button className="oh-filter-apply-btn" onClick={handleApplyFilters}>
              Search
            </button>
            {hasActiveFilter && (
              <button className="oh-filter-clear-btn" onClick={handleClearFilters} title="Clear filters">
                <FiRefreshCw /> Clear
              </button>
            )}
          </div>

          {loading ? (
            <div className="oh-loader">
              <div className="oh-spinner" />
              <p>Loading inquiries…</p>
            </div>
          ) : (
            <div className="oh-table-wrap">
              <table className="oh-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Customer</th>
                    <th>Vendor</th>
                    <th>Services</th>
                    <th>Event Date</th>
                    <th>Created</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {inquiries.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="oh-empty">No inquiries found.</td>
                    </tr>
                  ) : (
                    inquiries.map((inq, idx) => (
                      <tr key={inq._id}>
                        <td>{(page - 1) * limit + idx + 1}</td>
                        <td>
                          <div className="oh-user-cell">
                            <span className="oh-name">{inq.customer?.name || "N/A"}</span>
                            <span className="oh-email">{inq.customer?.mobile_number || ""}</span>
                          </div>
                        </td>
                        <td>
                          <div className="oh-user-cell">
                            <span className="oh-name">{inq.vendor?.name || "N/A"}</span>
                            <span className="oh-email">{inq.vendor?.mobile_number || ""}</span>
                          </div>
                        </td>
                        <td>
                          <div className="oh-services">
                            {(inq.services || []).slice(0, 2).map((s, i) => (
                              <span key={i} className="oh-service-tag">{s}</span>
                            ))}
                            {inq.services?.length > 2 && (
                              <span className="oh-service-more">+{inq.services.length - 2}</span>
                            )}
                          </div>
                        </td>
                        <td>{fmt(inq.eventDate)}</td>
                        <td>{fmt(inq.createdAt)}</td>
                        <td>
                          <span className={`oh-role-badge ${inq.createdByRole === "vendor" ? "oh-role-vendor" : "oh-role-customer"}`}>
                            {capitalize(inq.createdByRole)}
                          </span>
                        </td>
                        <td>
                          <span className={`oh-status ${statusClass(inq.status)}`}>
                            {capitalize(inq.status)}
                          </span>
                        </td>
                        <td>
                          <button
                            className="oh-icon-btn oh-view-btn"
                            title="View Details"
                            onClick={() => setSelected(inq)}
                          >
                            <FiEye />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ---- PAGINATION ---- */}
          <div className="oh-pagination">
            <button
              className="oh-pag-btn"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <FiChevronLeft /> Prev
            </button>
            <span className="oh-pag-info">
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </span>
            <button
              className="oh-pag-btn"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* ====== VIEW DETAILS MODAL ====== */}
      {selected && (
        <div className="oh-overlay">
          <div className="oh-modal" ref={modalRef}>
            <div className="oh-modal-header">
              <h3>Inquiry Details</h3>
              <button className="oh-modal-close" onClick={() => setSelected(null)}>
                <FiX />
              </button>
            </div>

            <div className="oh-modal-body">
              {/* Status + Role */}
              <div className="oh-modal-badges">
                <span className={`oh-status ${statusClass(selected.status)}`}>
                  {capitalize(selected.status)}
                </span>
                <span className={`oh-role-badge ${selected.createdByRole === "vendor" ? "oh-role-vendor" : "oh-role-customer"}`}>
                  By: {capitalize(selected.createdByRole)}
                </span>
              </div>

              {/* Dates */}
              <div className="oh-modal-section">
                <div className="oh-modal-row">
                  <span className="oh-modal-icon"><FiCalendar /></span>
                  <div>
                    <div className="oh-modal-field-label">Event Date</div>
                    <div className="oh-modal-field-value">{fmt(selected.eventDate)}</div>
                  </div>
                </div>
                <div className="oh-modal-row">
                  <span className="oh-modal-icon"><FiCalendar /></span>
                  <div>
                    <div className="oh-modal-field-label">Created At</div>
                    <div className="oh-modal-field-value">{fmt(selected.createdAt)}</div>
                  </div>
                </div>
              </div>

              {/* People */}
              <div className="oh-modal-grid">
                <div className="oh-modal-person-card">
                  <div className="oh-modal-person-title">
                    <FiUser /> Customer
                  </div>
                  <div className="oh-modal-person-name">{selected.customer?.name || "N/A"}</div>
                  <div className="oh-modal-person-detail">{selected.customer?.email || ""}</div>
                  <div className="oh-modal-person-detail">{selected.customer?.mobile_number || ""}</div>
                </div>
                <div className="oh-modal-person-card oh-vendor-card">
                  <div className="oh-modal-person-title">
                    <FiUser /> Vendor
                  </div>
                  <div className="oh-modal-person-name">{selected.vendor?.name || "N/A"}</div>
                  <div className="oh-modal-person-detail">{selected.vendor?.email || ""}</div>
                  <div className="oh-modal-person-detail">{selected.vendor?.mobile_number || ""}</div>
                </div>
              </div>

              {/* Services */}
              <div className="oh-modal-services-section">
                <div className="oh-modal-services-title">
                  <FiShoppingBag /> Services
                </div>
                <div className="oh-modal-services-list">
                  {(selected.services || []).map((s, i) => (
                    <span key={i} className="oh-service-tag oh-service-tag-lg">{s}</span>
                  ))}
                </div>
              </div>

              {/* Message */}
              {selected.message && (
                <div className="oh-modal-message">
                  <div className="oh-modal-services-title">Message</div>
                  <p>{selected.message}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderHistory;
